import { useState } from "react";
import { fileUrl } from "@/lib/api";
import { ChevronLeft, ChevronRight, Smartphone, Expand } from "lucide-react";

export const ListingCard = ({ listing: l, phoneTel, onZoom }) => {
  const imgs = l.images && l.images.length ? l.images : l.image_url ? [l.image_url] : [];
  const [idx, setIdx] = useState(0);
  const cur = imgs[idx];

  return (
    <div data-testid={`listing-${l.id}`} className="rounded-2xl border border-white/[0.07] bg-[#101010] overflow-hidden flex flex-col">
      <div className="h-48 bg-[#0d0d0d] flex items-center justify-center p-3 relative group">
        {cur ? (
          <img src={fileUrl(cur)} alt={l.name} onClick={() => onZoom(imgs, idx)} data-testid={`listing-img-${l.id}`}
            className="h-full object-contain cursor-zoom-in" />
        ) : <Smartphone className="w-10 h-10 text-neutral-700" />}

        {cur && (
          <button onClick={() => onZoom(imgs, idx)} className="absolute bottom-2 right-2 w-8 h-8 rounded-full bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <Expand className="w-4 h-4" />
          </button>
        )}

        {imgs.length > 1 && (
          <>
            <button onClick={() => setIdx((i) => (i - 1 + imgs.length) % imgs.length)} data-testid={`listing-prev-${l.id}`}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/60 flex items-center justify-center hover:bg-black/80 transition-colors"><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={() => setIdx((i) => (i + 1) % imgs.length)} data-testid={`listing-next-${l.id}`}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/60 flex items-center justify-center hover:bg-black/80 transition-colors"><ChevronRight className="w-4 h-4" /></button>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
              {imgs.map((_, i) => <span key={i} className={`w-1.5 h-1.5 rounded-full transition-colors ${i === idx ? "bg-white" : "bg-white/30"}`} />)}
            </div>
          </>
        )}

        {!l.in_stock && <span className="absolute top-3 left-3 text-[10px] px-2 py-1 rounded-full bg-black/70 text-neutral-300">Satıldı</span>}
      </div>

      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <h3 className="font-medium">{l.name}</h3>
          <span className="text-xs text-neutral-500">{l.storage}{l.color ? ` · ${l.color}` : ""}</span>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-3">
          {l.condition && <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/5 text-neutral-300">Vəziyyət: {l.condition}</span>}
          {l.sim_type && { dual: "Fiziki SIM + eSIM", dualsim: "Dual SIM + eSIM", esim: "Yalnız eSIM" }[l.sim_type] && (
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/5 text-neutral-300">{{ dual: "Fiziki SIM + eSIM", dualsim: "Dual SIM + eSIM", esim: "Yalnız eSIM" }[l.sim_type]}</span>
          )}
          <span className={`text-[11px] px-2 py-0.5 rounded-full ${l.battery_health ? (Number(l.battery_health) >= 90 ? "bg-[#30D158]/15 text-[#30D158]" : "bg-[#FF9F0A]/15 text-[#FF9F0A]") : (l.battery_replaced ? "bg-[#FF9F0A]/15 text-[#FF9F0A]" : "bg-white/5 text-neutral-400")}`}>{l.battery_health ? `Batareya: ${l.battery_health}%` : (l.battery_replaced ? "Batareya: dəyişilib" : "Batareya: orijinal")}</span>
          {l.battery_health && <span className={`text-[11px] px-2 py-0.5 rounded-full ${l.battery_replaced ? "bg-[#FF9F0A]/15 text-[#FF9F0A]" : "bg-white/5 text-neutral-400"}`}>{l.battery_replaced ? "Batareya dəyişilib" : "Batareya orijinal"}</span>}
          <span className={`text-[11px] px-2 py-0.5 rounded-full ${l.faceid_working ? "bg-white/5 text-neutral-400" : "bg-red-500/15 text-red-400"}`}>Face ID: {l.faceid_working ? "işlək" : "yox"}</span>
          {l.scuffs && <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/5 text-neutral-400">Cızıq var</span>}
          {l.chips && <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/5 text-neutral-400">Əziyi var</span>}
          <span className={`text-[11px] px-2 py-0.5 rounded-full ${l.registered === false ? "bg-red-500/15 text-red-400" : "bg-white/5 text-neutral-400"}`}>Qeydiyyat: {l.registered === false ? "yox" : "var"}</span>
          <span className={`text-[11px] px-2 py-0.5 rounded-full ${l.beyanname === false ? "bg-red-500/15 text-red-400" : "bg-white/5 text-neutral-400"}`}>Bəyannamə: {l.beyanname === false ? "yox" : "var"}</span>
          <span className={`text-[11px] px-2 py-0.5 rounded-full ${l.wifi_working === false ? "bg-red-500/15 text-red-400" : "bg-white/5 text-neutral-400"}`}>Wi‑Fi: {l.wifi_working === false ? "yox" : "işlək"}</span>
        </div>
        {l.description && <p className="text-xs text-neutral-500 mt-3">{l.description}</p>}
        <div className="mt-auto pt-4 flex items-center justify-between">
          <div className="font-display text-2xl font-medium">{(l.price || 0).toLocaleString("ru-RU")} <span className="text-neutral-500 text-lg">₼</span></div>
          <a href={phoneTel} className="text-xs px-4 py-2 rounded-full bg-neutral-100 text-black font-medium hover:bg-white transition-colors">Əlaqə</a>
        </div>
      </div>
    </div>
  );
};
